/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { ComponentProps, ComponentType, SVGProps } from "react"

import { cn } from "@/utils/cn"

import { buttonVariants } from "../primitives/button"

interface Props extends ComponentProps<"a"> {
    /**
     * Absolute URL of the external site.
     */
    href: string
    /**
     * Site logo shown before the label.
     *
     * Greyscale by default and in colour on hover or keyboard focus.
     */
    icon?: ComponentType<SVGProps<SVGSVGElement>>
}

/**
 * Renders an external link with the appearance of an outline button.
 *
 * Opens in a new tab and announces this to screen readers. An optional site
 * logo appears before the label and is hidden from assistive technology.
 *
 * Anchor props are passed through, except `target` and `rel`, which are fixed
 * to ensure safe new-tab navigation. `className` can extend or override the
 * default button styles.
 *
 * @example
 * <ExternalLink href="https://www.virustotal.com/" icon={VirusTotalLogo}>
 *     VirusTotal
 * </ExternalLink>
 */
export const ExternalLink = ({ className, children, icon: Icon, ...props }: Props) => (
    <a
        {...props}
        target="_blank"
        rel="noopener noreferrer"
        // Keep the link's appearance consistent with the button primitives during the Bootstrap migration.
        className={cn(buttonVariants({ variant: "outline", size: "sm" }), "text-foreground no-underline", className)}
    >
        {Icon && (
            <Icon
                data-icon="inline-start"
                aria-hidden
                className="opacity-75 grayscale transition-[filter,opacity] group-hover/button:opacity-100 group-hover/button:grayscale-0 group-focus-visible/button:opacity-100 group-focus-visible/button:grayscale-0"
            />
        )}
        {children}
        <span className="sr-only"> (opens in a new tab)</span>
    </a>
)
